import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Plus, X, RotateCw } from "lucide-react";

interface PackingCard {
  id: number;
  title: string;
  subtitle: string;
  img: string;
  handwrittenText: string;
  handwrittenAuthor: string;
  rotate: string;
  floatDelay: number;
  floatDuration: number;
}

const packingCards: PackingCard[] = [
  {
    id: 1,
    title: "1. The French Linen",
    subtitle: "Clarity Polish",
    img: "https://lh3.googleusercontent.com/d/1W-IZi_9N46GwZtzUd-06ImPHjf1PhwAv",
    handwrittenText: "Every order is packed one at a time. A folded note goes in first, then a nest of crinkle paper to cradle the bottle - tied with twine, the way you’d wrap something meant for someone.",
    handwrittenAuthor: "",
    rotate: "-6deg",
    floatDelay: 0,
    floatDuration: 8,
  },
  {
    id: 2,
    title: "2. The Wax Stamp",
    subtitle: "Ancient Seal",
    img: "https://lh3.googleusercontent.com/d/1jt4bldQQM6nVsFuPUiuLbMo42CUq-TiX",
    handwrittenText: "Each box is labelled by hand and numbered - one of only ten of its fragrance. The day it was blended is printed on the side, so you always know when your scent was made.",
    handwrittenAuthor: "",
    rotate: "8deg",
    floatDelay: 1.5,
    floatDuration: 9,
  },
  {
    id: 3,
    title: "3. The Inscription",
    subtitle: "Unique Signature",
    img: "https://lh3.googleusercontent.com/d/10gBmfUMQLDE7_FcPlH2BneTJ6Yyhe9hv",
    handwrittenText: "Wrapped to arrive whole. Every bottle is wrapped and cushioned before it leaves the table - layer over layer, so your perfume reach you exactly as it left this table.",
    handwrittenAuthor: "",
    rotate: "-4deg",
    floatDelay: 0.8,
    floatDuration: 8.5,
  },
  {
    id: 4,
    title: "4. Scented Enclosure",
    subtitle: "Raw Iris Mist",
    img: "https://lh3.googleusercontent.com/d/16k3I3P9sxwplTOv0MCQD61B2hqcHPy4Y",
    handwrittenText: "Once sealed, each parcel is marked fragile & treated that way. What’s inside took over 1,400 hours to perfect the last few feet of its journey deserves the same attention.",
    handwrittenAuthor: "",
    rotate: "5deg",
    floatDelay: 2.2,
    floatDuration: 10,
  }
];

// Helper to get responsive position classes that position images in the 4 corners, slightly closer to the edges to avoid overlap
const getPositionClass = (id: number) => {
  switch (id) {
    case 1:
      // Top Left - pushed to corners
      return "top-[1%] left-[1%] sm:top-[3%] sm:left-[3%] md:top-[5%] md:left-[5%] lg:top-[7%] lg:left-[8%]";
    case 2:
      // Top Right - pushed to corners
      return "top-[1%] right-[1%] sm:top-[3%] sm:right-[3%] md:top-[5%] md:right-[5%] lg:top-[7%] lg:right-[8%]";
    case 3:
      // Bottom Left - pushed to corners
      return "-bottom-[3%] left-[1%] sm:bottom-0 sm:left-[3%] md:bottom-[1%] md:left-[5%] lg:bottom-[2%] lg:left-[8%]";
    case 4:
      // Bottom Right - pushed to corners
      return "-bottom-[3%] right-[1%] sm:bottom-0 sm:right-[3%] md:bottom-[1%] md:right-[5%] lg:bottom-[2%] lg:right-[8%]";
    default:
      return "";
  }
};

// Helper to place the "+" hotspot near the corner facing the center
const getPlusPositionClass = (id: number) => {
  switch (id) {
    case 1:
      // Card 1 (Top Left): Faces center at bottom-right corner
      return "bottom-3 right-3 sm:bottom-4 sm:right-4";
    case 2:
      // Card 2 (Top Right): Faces center at bottom-left corner
      return "bottom-3 left-3 sm:bottom-4 sm:left-4";
    case 3:
      // Card 3 (Bottom Left): Faces center at top-right corner
      return "top-3 right-3 sm:top-4 sm:right-4";
    case 4:
      // Card 4 (Bottom Right): Faces center at top-left corner
      return "top-3 left-3 sm:top-4 sm:left-4";
    default:
      return "bottom-3 right-3";
  }
};

export const PackingTableSection: React.FC = () => {
  const [activeCard, setActiveCard] = useState<PackingCard | null>(null);
  const [isFlipped, setIsFlipped] = useState<boolean>(false);

  const handleOpen = (card: PackingCard) => {
    setActiveCard(card);
    setIsFlipped(false);
    // Slight delay for the 3D flip effect after floating to center
    setTimeout(() => {
      setIsFlipped(true);
    }, 500);
  };

  const handleClose = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setActiveCard(null);
    }, 400);
  };

  return (
    <section className="relative w-full bg-black text-white py-20 md:py-28 px-4 overflow-hidden select-none">
      {/* Dynamic Google handwriting font integration */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');
        .font-handwritten {
          font-family: 'Caveat', cursive;
        }
      `}} />

      {/* Decorative ambient background vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.015)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 flex flex-col items-center">
        
        {/* Main Title Section */}
        <div className="text-center max-w-xl mb-12 md:mb-16 z-20">
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#DFB37A]/80 block mb-3">
            Behind the Scenes
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.1] text-white">
            The Packing <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">Table</span>
          </h2>
        </div>

        {/* Dynamic Canvas Container with solid black backdrop */}
        <div className="relative w-full min-h-[640px] sm:min-h-[780px] md:min-h-[920px] lg:min-h-[1000px] xl:min-h-[1080px] flex items-center justify-center overflow-visible">
          
          {/* Central static text modeled precisely after user's screenshot */}
          <div className="text-center max-w-xs sm:max-w-md md:max-w-lg px-6 pointer-events-none z-10">
            <p className="text-lg sm:text-xl md:text-2xl font-normal text-white/90 leading-relaxed tracking-tight">
              Each unique limited piece is guided by seasoned hands on our oak workbench, because <span className="italic font-serif text-[#DFB37A]">human spirit</span> is the final ingredient of true high perfumery.
            </p>
          </div>

          {/* Render the 4 Corner Floating Cards */}
          {packingCards.map((card) => {
            const positionClass = getPositionClass(card.id);
            const plusClass = getPlusPositionClass(card.id);

            return (
              <motion.div
                key={card.id}
                className={`absolute cursor-pointer group z-20 ${positionClass}`}
                animate={{
                  y: [0, -28, 0],
                  x: [0, 10, 0],
                  rotate: [
                    parseInt(card.rotate),
                    parseInt(card.rotate) + 2.5,
                    parseInt(card.rotate) - 2.5,
                    parseInt(card.rotate)
                  ],
                }}
                transition={{
                  duration: card.floatDuration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: card.floatDelay,
                }}
                onClick={() => handleOpen(card)}
                whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
              >
                {/* Clean Plain Image Frame with increased sizing, fully colored (no grayscale) and forced aspect ratio */}
                <div className="relative w-[140px] sm:w-[195px] md:w-[265px] lg:w-[315px] xl:w-[350px] aspect-[3/4] rounded-2xl overflow-hidden border border-neutral-900 bg-neutral-950/40 shadow-2xl transition-all duration-300 group-hover:border-[#DFB37A]/30">
                  <img
                    src={card.img}
                    alt={card.title}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover transition-all duration-700 opacity-100 filter-none group-hover:scale-105"
                  />

                  {/* Glassmorphic + Button placed strategically near the corner facing the center */}
                  <div className={`absolute ${plusClass} pointer-events-none`}>
                    <motion.div 
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center text-white shadow-xl pointer-events-auto hover:bg-[#DFB37A] hover:text-black hover:border-transparent hover:scale-110 transition-all duration-300"
                      whileTap={{ scale: 0.9 }}
                    >
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            );
          })}

        </div>
      </div>

      {/* Floating Interactive Flipping Overlay Modal */}
      <AnimatePresence>
        {activeCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            {/* Dark premium backdrop with heavy blur */}
            <div 
              className="absolute inset-0 bg-black/95 backdrop-blur-md"
              onClick={handleClose}
            />

            {/* Modal Container */}
            <motion.div
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 180 }}
              className="relative w-full max-w-[340px] md:max-w-[420px] aspect-[4/5] z-10"
              style={{ perspective: "1500px" }}
            >
              
              {/* Premium Frosted CTAs placed on top of the card, fixed in place for absolute reliability */}
              {/* Top Left: Flip Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFlipped(!isFlipped);
                }}
                className={`absolute top-6 left-6 z-30 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg cursor-pointer hover:scale-110 active:scale-95 ${
                  isFlipped
                    ? "bg-black/5 backdrop-blur-md border border-black/15 text-neutral-800 hover:bg-[#DFB37A] hover:text-black hover:border-transparent"
                    : "bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-[#DFB37A] hover:text-black hover:border-transparent"
                }`}
                title={isFlipped ? "Show image" : "Show handwritten note"}
              >
                <RotateCw className="w-5 h-5" />
              </button>

              {/* Top Right: Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className={`absolute top-6 right-6 z-30 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg cursor-pointer hover:scale-110 active:scale-95 ${
                  isFlipped
                    ? "bg-black/5 backdrop-blur-md border border-black/15 text-neutral-800 hover:bg-[#DFB37A] hover:text-black hover:border-transparent"
                    : "bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-[#DFB37A] hover:text-black hover:border-transparent"
                }`}
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* 3D Flipping Card */}
              <motion.div
                className="w-full h-full relative"
                style={{ transformStyle: "preserve-3d" }}
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.65, ease: "easeInOut" }}
              >
                
                {/* FRONT SIDE (The Plain Image) */}
                <div 
                  className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-950 shadow-2xl"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <img
                    src={activeCard.img}
                    alt={activeCard.title}
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover p-4 opacity-100"
                  />
                </div>

                {/* BACK SIDE (White Hand-crafted Paper, Handwritten Text Only) */}
                <div 
                  className="absolute inset-0 w-full h-full rounded-2xl border border-neutral-300 bg-[#FAF9F6] shadow-2xl flex flex-col justify-center p-8 md:p-12 text-neutral-900"
                  style={{ 
                    backfaceVisibility: "hidden", 
                    transform: "rotateY(180deg)",
                    backgroundImage: "radial-gradient(#e4e3df 1px, transparent 1px)",
                    backgroundSize: "24px 24px"
                  }}
                >
                  {/* Handwritten Content Block */}
                  <div className="flex flex-col justify-center space-y-6">
                    {/* Genuine signature handwriting cursive with 20% size increase */}
                    <p className="font-handwritten text-2xl sm:text-3xl md:text-4xl text-neutral-900 leading-relaxed text-left antialiased font-semibold italic">
                      "{activeCard.handwrittenText}"
                    </p>
                    {/* Handwritten Author Signature */}
                    {activeCard.handwrittenAuthor && (
                      <p className="font-handwritten text-xl sm:text-2xl md:text-3xl text-[#9A7444] font-bold text-right self-end mt-2">
                        {activeCard.handwrittenAuthor}
                      </p>
                    )}
                  </div>
                </div>

              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
